import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpHeaders
} from '@angular/common/http';

import { environment } from '../../../environments/environment';
import {AccountService} from '../../services/account.service';
import {Application} from '../../models/ApplicationModel';
import {catchError, tap} from 'rxjs/operators';
import {Observable, of} from 'rxjs';
import {ChecklistConfigModel, ChecklistModel} from '../../models/ChecklistModel';
import {DocumentConfigModel, DocumentModel} from '../../models/DocumentModel';
import {FeeInfo, ProfessionalType, SystemSetting} from '../../models/CommonModels';
import {ProfessionalTypeConfig} from '../../models/ProfessionalType';

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json'
  })
};

@Injectable({
  providedIn: 'root'
})
export class SettingsService {

  private checklistConfigURL = environment.baseUrl + '/ChecklistSpec';
  private checklistURL = environment.baseUrl + '/Checklist';
  private documentsConfigURL = environment.baseUrl + '/DocumentSpec';
  private feeInfoURL = environment.baseUrl + '/FeeInfo';
  private documentsURL = environment.baseUrl + '/DocumentRef';
  private specificProductItemURL = environment.baseUrl + '/SpecificProductItem';
  private systemSettingsURL = environment.baseUrl + '/SystemSetting';
  private professionalConfigURL = environment.baseUrl + '/ProfessionalTypeConfig';

  constructor(private http: HttpClient, private accountService: AccountService) {
    httpOptions.headers = httpOptions.headers.set('Authorization', 'Bearer ' + this.accountService.getTokenData());
  }
  private log(message: string) {
    //this.messageService.add(`HeroService: ${message}`);
    console.log(`HeroService: ${message}`);
  }

  /**
   * Handle Http operation that failed.
   * Let the app continue.
   * @param operation - name of the operation that failed
   * @param result - optional value to return as the observable result
   */
  private handleError < T > (operation = 'operation', result ? : T) {
    return (error: any): Observable < T > => {

      // TODO: send the error to remote logging infrastructure
      console.error(error); // log to console instead

      // TODO: better job of transforming error for user consumption
      this.log(`${operation} failed: ${error.message}`);

      // Let the app keep running by returning an empty result.
      return of(result as T);
    };
  }

  getChangeChecklistConfigForFacilityTypeProductTypeAppCategoryFacilityCategoryAndChangeType(changeTypeRef){
    return this.http.post<ChecklistConfigModel[]>(this.checklistConfigURL + '/change',changeTypeRef, httpOptions);
  }

  getChecklistConfigForFacilityTypeProductTypeAppCategoryAndFacilityCategory(facilityTypeId, productTypeId, appCategoryId, facilityCategoryId,changeTypeRefID) {
    return this.http.get<ChecklistConfigModel[]>(this.checklistConfigURL + '/' + facilityTypeId + '/' + productTypeId
                                                              + '/' + appCategoryId + '/' + facilityCategoryId + '/' + changeTypeRefID, httpOptions);
  }

  getDocConfigForFacilityTypeProductTypeAppCategoryAndChangeType(facilityTypeId, productTypeId, applicationCategoryId, facilityCategoryId, 
    changeTypeRefId, manuLevelId, manuRiskLevelId) {
    return this.http.get<DocumentConfigModel[]>(this.documentsConfigURL + '/' + facilityTypeId + '/' + productTypeId
        + '/' + applicationCategoryId + '/' + facilityCategoryId + '/' + changeTypeRefId + '/' + manuLevelId + '/' + manuRiskLevelId, httpOptions);
  }

  getFeeInfoForSelection(facilityTypeId, productTypeId, applicationCategoryId, facilityCategoryId, changeTypeRefId, withinRegion){
    const filter = {
      'facilityTypeID': facilityTypeId,
      'productTypeID': productTypeId,
      'applicationCategoryID': applicationCategoryId,
      'facilityCategoryID': facilityCategoryId,
      'changeTypeRefID': changeTypeRefId,
      'withinRegion': withinRegion
    }
    return this.http.post<FeeInfo>(this.feeInfoURL + '/getByFilter', filter, httpOptions);
  }

  addChecklistConfig(checklistConfig: ChecklistConfigModel) {
    return this.http.post<ChecklistConfigModel>(this.checklistConfigURL, checklistConfig, httpOptions)
      .pipe(
        tap((checklistConfig1: ChecklistConfigModel) => this.log(`added checklist config w/ id=${checklistConfig1.checklistID}`)),
        catchError(this.handleError<ChecklistConfigModel> ('add checklistConfig'))
      );
  }

  addDocConfig(documentConfigModel: DocumentConfigModel) {
    return this.http.post<DocumentConfigModel>(this.documentsConfigURL, documentConfigModel, httpOptions)
      .pipe(
        tap((documentConfigModel1: DocumentConfigModel) => this.log(`added doc config w/ id=${documentConfigModel1.documentReferenceID}`)),
        catchError(this.handleError<DocumentConfigModel> ('add DocumentConfigModel'))
      );
  }

  removeChecklistConfig(checklistConfig: ChecklistConfigModel) {
    return this.http.delete<any>(this.checklistConfigURL + '/' + checklistConfig.id, httpOptions);
  }

  removeDocConfig(documentConfigModel: DocumentConfigModel) {
    return this.http.delete<any>(this.documentsConfigURL + '/' + documentConfigModel.id, httpOptions);
  }

  updateChecklistConfig(checklistConfig: ChecklistConfigModel) {
    return this.http.put<any>(this.checklistConfigURL, checklistConfig, httpOptions);
  }

  updateDocConfig(documentConfigModel: DocumentConfigModel) {
    return this.http.put<any>(this.documentsConfigURL, documentConfigModel, httpOptions);
  }

  addChecklist(checklist: ChecklistModel) {
    return this.http.post<ChecklistModel>(this.checklistURL, checklist, httpOptions)
      .pipe(
        tap((checklistModel: ChecklistModel) => this.log(`added checklist w/ name=${checklistModel.name}`)),
        catchError(this.handleError<ChecklistModel> ('add checklist'))
      );
  }

  addDoc(document: DocumentModel) {
    return this.http.post<DocumentModel>(this.documentsURL, document, httpOptions)
      .pipe(
        tap((document1: DocumentModel) => this.log(`added document w/ name=${document1.name}`)),
        catchError(this.handleError<DocumentModel> ('add document'))
      );
  }

  getAllChecklists() {
    return this.http.get<ChecklistModel[]>(this.checklistURL, httpOptions);
  }

  getAllDocuments() {
    return this.http.get<DocumentModel[]>(this.documentsURL, httpOptions);
  }

  addSpecificProductItem(specificProductItem) {
    return this.http.post<any>(this.specificProductItemURL, specificProductItem, httpOptions);
  }

  deleteSpecificProductItem(specificProductItemId) {
    return this.http.delete<any>(this.specificProductItemURL + '/' + specificProductItemId, httpOptions);
  }

  configureFeeForSelection(feeInfo) {
    return this.http.post<any>(this.feeInfoURL, feeInfo, httpOptions);
  }

  updateFeeConfigurationForSelection(feeInfo) {
    return this.http.put<any>(this.feeInfoURL, feeInfo, httpOptions);
  }

  getSystemSettings() {
    return this.http.get<SystemSetting[]>(this.systemSettingsURL, httpOptions);
  }

  saveSystemSetting(systemSetting) {
    return this.http.post<SystemSetting>(this.systemSettingsURL + '/InsertOrUpdate', systemSetting, httpOptions);
  }

  saveProfessionalConfig(professionalConfig) {
    return this.http.post<Boolean>(this.professionalConfigURL, professionalConfig, httpOptions);
  }

  updateProfessionalConfig(professionalConfig) {
    return this.http.put<Boolean>(this.professionalConfigURL, professionalConfig, httpOptions);
  }

  getProfessionalConfigListByFilter(filter) {
    return this.http.post<ProfessionalTypeConfig[]>(this.professionalConfigURL + '/FilterProfessional', filter, httpOptions);
  }

  getProfessionalConfigListForApplicant(filter) {
    return this.http.post<ProfessionalTypeConfig[]>(this.professionalConfigURL + '/GetConfiguredProfessionals', filter, httpOptions);
  }

  getConfiguredProfessionalTypes(filter) {
    return this.http.post<ProfessionalType[]>(this.professionalConfigURL + '/GetConfiguredProfessionalTypes', filter, httpOptions);
  }
}
